import React, { useState, useEffect } from 'react';
import {
  CurrencyDollarIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  PlusIcon,
  XMarkIcon,
  CpuChipIcon,
  EnvelopeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid
} from '@heroicons/react/24/solid';

import axiosInstance from '../api/axiosInstance';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import logger from '../../utils/logger';

// ERP User tier definitions with Tellency pricing (monthly / annual)
const erpTiers = {
  starter: { name: 'Starter', price: 79, annualPrice: 66, description: 'Core ERP modules, basic AI', maxUsers: 10 },
  professional: { name: 'Professional', price: 119, annualPrice: 99, description: 'All modules, advanced AI workflows', maxUsers: 50 },
  enterprise: { name: 'Enterprise', price: 0, annualPrice: 0, description: 'Custom pricing, unlimited users', maxUsers: null }
};

// Legacy tier mapping (monthly / annual)
const tierPriceMap = {
  monthly: { starter: 79, basic: 79, professional: 119, power: 119, enterprise: 0, admin: 0 },
  annually: { starter: 66, basic: 66, professional: 99, power: 99, enterprise: 0, admin: 0 }
};

// Available ERP modules
const availableModules = [
  { id: 'financial', name: 'Financial', description: 'Accounting, invoicing, payments' },
  { id: 'sales', name: 'Sales', description: 'Pipeline, lead scoring, analytics' },
  { id: 'crm', name: 'CRM', description: 'Customer management, campaigns' },
  { id: 'inventory', name: 'Inventory', description: 'Stock control, warehousing' },
  { id: 'hr', name: 'HR', description: 'Payroll, attendance, employee management' },
  { id: 'projects', name: 'Projects', description: 'Kanban boards, collaboration' },
  { id: 'manufacturing', name: 'Manufacturing', description: 'BOM, production planning' },
  { id: 'procurement', name: 'Procurement', description: 'Purchase orders, vendor management' },
  { id: 'pos', name: 'POS', description: 'Point of sale, retail management' }
];

const LicenseManagement = ({ showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [businessModules, setBusinessModules] = useState([]);
  const [modulesChanged, setModulesChanged] = useState(false);
  const [savingModules, setSavingModules] = useState(false);

  // Agent panel state
  const [showAgentPanel, setShowAgentPanel] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [overviewRes, businessRes] = await Promise.all([
        axiosInstance.get('/api/licenses/overview').catch(() => ({ data: null })),
        axiosInstance.get('/api/business').catch(() => ({ data: null }))
      ]);

      if (overviewRes.data?.success) {
        setOverview(overviewRes.data);
      }

      if (businessRes.data?.success) {
        setBusinessModules(businessRes.data.business?.erpModules?.modules || []);
      }
    } catch (error) {
      logger.error('Error fetching license data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    setBusinessModules(prev => {
      const updated = prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId];
      setModulesChanged(true);
      return updated;
    });
  };

  const saveModules = async () => {
    try {
      setSavingModules(true);
      const response = await axiosInstance.post('/api/business/erp-modules', {
        modules: businessModules,
        enabled: businessModules.length > 0,
        tier: 'standard'
      });
      if (response.data.success) {
        showNotification('ERP modules updated successfully', 'success');
        setModulesChanged(false);
        fetchData();
      } else {
        showNotification(response.data.message || 'Failed to update modules', 'error');
      }
    } catch (error) {
      showNotification('Error updating modules', 'error');
    } finally {
      setSavingModules(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  const platform = overview?.platform || { users: 0, pricePerUser: 3, monthlyCost: 0 };
  const erpUsers = overview?.erpUsers || { total: 0, monthlyCost: 0, seats: [] };
  const aiAgents = overview?.aiAgents || { count: 0, pricePerAgent: 199, monthlyCost: 0 };
  const billing = overview?.billing || { totalMonthlyCost: 0 };
  const billingCycle = billing.billingCycle || 'monthly';
  const isAnnual = billingCycle === 'annually';
  const periodLabel = isAnnual ? 'yr' : 'mo';
  const periodName = isAnnual ? 'annual' : 'monthly';

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CurrencyDollarIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Licenses & Billing</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
              ${isAnnual ? (billing.totalMonthlyCost * 12).toFixed(2) : billing.totalMonthlyCost.toFixed(2)}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">/{isAnnual ? 'yr' : 'mo'}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto p-6 space-y-6">

        {/* License Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* Platform License Card */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] overflow-hidden">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <EnvelopeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Active
                </span>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Platform</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Email hosting, domains, storage</p>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{platform.users}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">users</span>
              </div>
            </div>
            <div className="px-5 py-3 bg-gray-50 dark:bg-white/[0.02] border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">${platform.pricePerUser}/user/{periodLabel}</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                ${platform.monthlyCost.toFixed(2)}/{periodLabel}
              </span>
            </div>
          </div>

          {/* AI Agents License Card */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] overflow-hidden">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <CpuChipIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                {aiAgents.count > 0 ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    Not configured
                  </span>
                )}
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">AI Agents</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Autonomous AI workers</p>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{aiAgents.count}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">{aiAgents.count === 1 ? 'agent' : 'agents'}</span>
              </div>
            </div>
            <div className="px-5 py-3 bg-gray-50 dark:bg-white/[0.02] border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <button
                onClick={() => setShowAgentPanel(true)}
                className="text-xs font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300"
              >
                View Details
              </button>
              <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                ${aiAgents.monthlyCost.toFixed(2)}/{periodLabel}
              </span>
            </div>
          </div>

          {/* ERP Users License Card */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] overflow-hidden">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <UserGroupIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                {erpUsers.total > 0 ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    No seats
                  </span>
                )}
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">ERP Users</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Tellency seat licenses</p>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{erpUsers.total}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">{erpUsers.total === 1 ? 'seat' : 'seats'}</span>
              </div>
            </div>
            <div className="px-5 py-3 bg-gray-50 dark:bg-white/[0.02] border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">${isAnnual ? '$66–$99' : '$79–$119'}/user/{periodLabel}</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                ${erpUsers.monthlyCost.toFixed(2)}/{periodLabel}
              </span>
            </div>
          </div>
        </div>

        {/* ERP User Seats Table */}
        {erpUsers.seats.length > 0 && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserGroupIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">ERP User Seats</h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">({erpUsers.total})</span>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-white/[0.02]">
                  <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                  <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tier</th>
                  <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {erpUsers.seats.map(seat => {
                  const tierName = erpTiers[seat.tier]?.name || seat.tier;
                  const prices = tierPriceMap[billingCycle] || tierPriceMap.monthly;
                  const tierPrice = prices[seat.tier] || (isAnnual ? 66 : 79);
                  return (
                    <tr key={seat.id} className="hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                      <td className="px-5 py-2.5">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{seat.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{seat.email}</p>
                        </div>
                      </td>
                      <td className="px-5 py-2.5">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 capitalize">{tierName}</span>
                      </td>
                      <td className="px-5 py-2.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          seat.status === 'active'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {seat.status === 'active' ? 'Active' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-5 py-2.5 text-right text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                        ${tierPrice}/{periodLabel}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ERP Modules Section */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BuildingOfficeIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">ERP Modules</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {businessModules.length} active
              </span>
            </div>
            {modulesChanged && (
              <button
                onClick={saveModules}
                disabled={savingModules}
                className="px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {savingModules ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableModules.map(mod => {
                const isActive = businessModules.includes(mod.id);
                return (
                  <button
                    key={mod.id}
                    onClick={() => toggleModule(mod.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      isActive
                        ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10'
                        : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                    }`}>
                      {isActive ? (
                        <CheckCircleIconSolid className="w-4 h-4" />
                      ) : (
                        <PlusIcon className="w-4 h-4" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${
                        isActive ? 'text-orange-700 dark:text-orange-300' : 'text-gray-900 dark:text-white'
                      }`}>{mod.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{mod.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Billing Breakdown */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Billing Summary</h3>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Platform ({platform.users} users × ${platform.pricePerUser})
                </span>
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                ${platform.monthlyCost.toFixed(2)}/{periodLabel}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  AI Agents ({aiAgents.count} × ${aiAgents.pricePerAgent})
                </span>
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                ${aiAgents.monthlyCost.toFixed(2)}/{periodLabel}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  ERP Users ({erpUsers.total} seats)
                </span>
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                ${erpUsers.monthlyCost.toFixed(2)}/{periodLabel}
              </span>
            </div>

            <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <div>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {isAnnual ? 'Total annual' : 'Total monthly'}
                </span>
                {isAnnual && (
                  <p className="text-xs text-gray-400">${billing.totalMonthlyCost.toFixed(2)}/{periodLabel} × 12</p>
                )}
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                ${isAnnual ? (billing.totalMonthlyCost * 12).toFixed(2) : billing.totalMonthlyCost.toFixed(2)}
              </span>
            </div>

            {billing.nextBillingDate && (
              <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                Next billing: {new Date(billing.nextBillingDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        {/* ERP Pricing Tiers Reference */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">ERP Pricing Tiers</h3>
            </div>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(erpTiers).map(([key, tier]) => (
                <div
                  key={key}
                  className={`rounded-lg border p-4 ${
                    key === 'professional'
                      ? 'border-orange-300 dark:border-orange-700 bg-orange-50/50 dark:bg-orange-900/5'
                      : 'border-gray-200 dark:border-gray-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{tier.name}</h4>
                    {key === 'professional' && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-0.5 mb-1">
                    {(isAnnual ? tier.annualPrice : tier.price) > 0 ? (
                      <>
                        <span className="text-xl font-bold text-gray-900 dark:text-white">${isAnnual ? tier.annualPrice : tier.price}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">/user/{periodLabel}</span>
                        {isAnnual && tier.price > 0 && (
                          <span className="text-xs text-green-600 dark:text-green-400 ml-1">
                            (save ${tier.price - tier.annualPrice})
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xl font-bold text-gray-900 dark:text-white">Custom</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{tier.description}</p>
                  {tier.maxUsers && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Up to {tier.maxUsers} users</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI Agent Details Panel (slide-over) */}
      {showAgentPanel && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setShowAgentPanel(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md">
            <div className="w-full h-full bg-white dark:bg-[#1a1a1a] shadow-2xl flex flex-col">
              {/* Panel Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">AI Agent Licenses</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {aiAgents.count} {aiAgents.count === 1 ? 'agent' : 'agents'} configured &middot; ${aiAgents.pricePerAgent}/agent/{periodLabel}
                  </p>
                </div>
                <button
                  onClick={() => setShowAgentPanel(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Panel Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Configured Agents List */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Configured Agents
                  </label>
                  {aiAgents.agents && aiAgents.agents.length > 0 ? (
                    <div className="space-y-2">
                      {aiAgents.agents.map((agent, i) => (
                        <div
                          key={agent.id || i}
                          className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#222222]"
                        >
                          <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                            <CpuChipIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{agent.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                              {agent.department}{agent.email ? ` · ${agent.email}` : ''}
                            </p>
                          </div>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            agent.status === 'active'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>
                            {agent.status === 'active' ? 'Active' : 'Paused'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <CpuChipIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No agents configured</p>
                      <p className="text-xs mt-1">Set up agents in the AI Agents section</p>
                    </div>
                  )}
                </div>

                {/* What's included */}
                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Each AI Agent includes:</p>
                  <ul className="space-y-1.5">
                    {[
                      'Autonomous task execution',
                      'Natural language ERP control',
                      'Workflow automation',
                      'Email & communication handling',
                      'Data analysis & reporting',
                      'Integration management'
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                        <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Cost summary */}
                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Cost Summary</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Active agents</span>
                      <span className="text-gray-900 dark:text-white font-medium">{aiAgents.count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Price per agent</span>
                      <span className="text-gray-900 dark:text-white font-medium">${aiAgents.pricePerAgent}/{periodLabel}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-gray-900 dark:text-white font-semibold">{isAnnual ? 'Annual' : 'Monthly'} cost</span>
                      <span className="text-gray-900 dark:text-white font-bold tabular-nums">
                        ${isAnnual ? (aiAgents.monthlyCost * 12).toFixed(2) : aiAgents.monthlyCost.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Panel Footer */}
              <div className="border-t border-gray-200 dark:border-gray-800 px-6 py-4">
                <button
                  onClick={() => setShowAgentPanel(false)}
                  className="w-full px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#222222] border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LicenseManagement;
